/** Preview sound: one remembered preference (localStorage, default ON) for the clip previews (Choose, Clip page).
 * Browsers refuse to autoplay with sound until the user interacts with the page; the controller then plays muted
 * (`soundBlocked`) and the first click or key press anywhere on the page brings the sound back when the preference
 * is ON. */
import { useEffect } from "react";
import type { FrameController } from "./controller";

export const SOUND_KEY = "vp.preview.sound";
export const SOUND_TOGGLE_TESTID = "preview-sound";

export function readSoundPref(): boolean {
  try {
    return globalThis.localStorage?.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

export function writeSoundPref(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(SOUND_KEY, on ? "on" : "off");
  } catch {
    // private window / blocked storage: the toggle still works for this page
  }
}

/** Apply the remembered preference to this player and unmute on the page's first user gesture (pointerdown /
 * keydown) when the preference is ON. A gesture on the Sound toggle itself is left to the toggle. */
export function usePreviewSound(ctrl: FrameController): void {
  useEffect(() => {
    ctrl.setMuted(!readSoundPref());
  }, [ctrl]);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const off = () => {
      document.removeEventListener("pointerdown", onGesture, true);
      document.removeEventListener("keydown", onGesture, true);
    };
    function onGesture(e: Event) {
      const t = e.target as Element | null;
      if (t && typeof t.closest === "function" && t.closest(`[data-testid="${SOUND_TOGGLE_TESTID}"]`)) return;
      off();
      if (readSoundPref() && ctrl.getState().muted) ctrl.setMuted(false);
    }
    document.addEventListener("pointerdown", onGesture, true);
    document.addEventListener("keydown", onGesture, true);
    return off;
  }, [ctrl]);
}
